import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { combineReducers } from 'redux';
import { connect } from 'react-redux';
import { createBrowserHistory } from 'history';
import { IntlProvider } from 'react-intl';
import queryString from 'query-string';
import { QueryClientProvider } from 'react-query';
import { ApolloProvider } from '@apollo/client';

import { ErrorBoundary, Loading } from '@folio/stripes-components';
import { metadata, icons, branding } from 'stripes-config';

/* ConnectContext - formerly known as RootContext, now comes from stripes-connect, so stripes-connect
 * is providing the infrastructure for store connectivity to the system. This eliminates a circular
 * dependency between stripes-connect and stripes-core. STCON-76
 */
import { ConnectContext } from '@folio/stripes-connect';
import initialReducers from '../../initialReducers';
import enhanceReducer from '../../enhanceReducer';
import createApolloClient from '../../createApolloClient';
import createReactQueryClient from '../../createReactQueryClient';
import {
  setSinglePlugin,
  setBindings,
  setIsAuthenticated,
  setOkapiToken,
  setTimezone,
  setCurrency,
  updateCurrentUser,
  setTranslations,
} from '../../okapiActions';
import {
  loadTranslations,
  checkOkapiSession,
  addRtrEventListeners,
} from '../../loginServices';
import { getQueryResourceKey, getCurrentModule } from '../../locationService';
import Stripes from '../../Stripes';
import RootWithIntl from '../../RootWithIntl';
import SystemSkeleton from '../SystemSkeleton';

import './Root.css';

import { withModules } from '../Modules';
import { FFetch } from './FFetch';
import OrgLoader from '../kware-components/OrgLoader/OrgLoader';

if (!metadata) {
  // eslint-disable-next-line no-console
  console.error(
    'No metadata harvested from package files, so you will not get app icons. Probably the stripes-core in your Stripes CLI is too old. Try `yarn global upgrade @folio/stripes-cli`',
  );
}

class Root extends Component {
  constructor(...args) {
    super(...args);

    const { modules, history, okapi, store } = this.props;

    this.reducers = { ...initialReducers };
    this.epics = {};
    this.withOkapi = okapi.withoutOkapi !== true;

    const appModule = getCurrentModule(modules, history.location);
    this.queryResourceStateKey = appModule
      ? getQueryResourceKey(appModule)
      : null;
    this.defaultRichTextElements = {
      b: (chunks) => <b>{chunks}</b>,
      i: (chunks) => <i>{chunks}</i>,
      em: (chunks) => <em>{chunks}</em>,
      strong: (chunks) => <strong>{chunks}</strong>,
      span: (chunks) => <span>{chunks}</span>,
      div: (chunks) => <div>{chunks}</div>,
      p: (chunks) => <p>{chunks}</p>,
      ul: (chunks) => <ul>{chunks}</ul>,
      ol: (chunks) => <ol>{chunks}</ol>,
      li: (chunks) => <li>{chunks}</li>,
      code: (chunks) => <code>{chunks}</code>,
    };

    this.apolloClient = createApolloClient(okapi);
    this.reactQueryClient = createReactQueryClient();

    // enhanced security mode:
    // * configure fetch and xhr interceptors to conduct RTR
    // * configure document-level event listeners to listen for RTR events
    if (this.props.config.useSecureTokens) {
      this.ffetch = new FFetch({ logger: this.props.logger });
      addRtrEventListeners(okapi, store);
    }
  }

  getChildContext() {
    return { addReducer: this.addReducer, addEpic: this.addEpic };
  }

  componentDidMount() {
    const { okapi, store, locale, defaultTranslations } = this.props;
    if (this.withOkapi) {
      // checkOkapiSession is async and will cause a re-render after a delay
      // when an API call's .then() handler updates the store.
      checkOkapiSession(okapi.url, store, okapi.tenant);
    }
    // TODO: remove this after we load locale and translations at start from a public endpoint
    loadTranslations(store, locale, defaultTranslations);
    
    /** kware start edit - Apply theme colors from stripes config to CSS variables */
    this.applyThemeColors();
    /** kware end edit */

    /** kware start edit - Apply saved font size preference */
    this.applyFontSizePreference();
    /** kware end edit */
  }

  /** kware start edit - Apply theme colors from stripes config to CSS variables */
  applyThemeColors = () => {
    const root = document.documentElement;
    if (branding?.theme) {
      root.style.setProperty('--primary', branding.theme.primaryColor || '#3992a9');
      root.style.setProperty('--main-nav-background-color', branding.theme.mainNavBackgroundColor || '#3992a4');
    }
  };
  /** kware end edit */

  /** kware start edit - Apply saved font size preference */
  applyFontSizePreference = () => {
    const FONT_SIZE_KEY = 'folio-user-font-size-preference';
    const fontSizeScales = {
      small: 0.875,
      medium: 1.0,
      large: 1.125,
      xlarge: 1.25
    };
    const saved = localStorage.getItem(FONT_SIZE_KEY);
    if (saved && fontSizeScales[saved]) {
      document.documentElement.style.fontSize = `${fontSizeScales[saved] * 100}%`;
    }
  };
  /** kware end edit */

  shouldComponentUpdate(nextProps) {
    return !this.withOkapi || nextProps.okapiReady || nextProps.serverDown;
  }

  addReducer = (key, reducer) => {
    if (this.queryResourceStateKey === key) {
      const originalReducer = reducer;
      const initialQueryObject = queryString.parse(window.location.search);
      // eslint-disable-next-line no-param-reassign
      reducer = (
        state = Object.values(initialQueryObject).length
          ? initialQueryObject
          : undefined,
        action,
      ) => originalReducer(state, action);
    }

    if (this.reducers[key] === undefined) {
      this.reducers[key] = reducer;
      this.props.store.replaceReducer(
        enhanceReducer(combineReducers({ ...this.reducers })),
      );
      return true;
    }
    return false;
  };

  addEpic = (key, epic) => {
    if (this.epics[key] === undefined) {
      this.epics[key] = epic;
      this.props.epics.add(epic);
      return true;
    }
    return false;
  };

  /* kware start editing --
   * Adding setTranslation func to  the stripes object, so we can update translation on the fly enytime
   */
  setLocaleTranslations = (store, newTrans) => {
    if (store.getState().okapi.token !== undefined) {
      store.dispatch(
        setTranslations(
          Object.assign(store.getState().okapi.translations, newTrans),
        ),
      );
    }
  };
  /* kware end editing */

  render() {
    const {
      logger,
      store,
      epics,
      config,
      okapi,
      actionNames,
      token,
      isAuthenticated,
      disableAuth,
      currentUser,
      currentPerms,
      locale,
      defaultTranslations,
      timezone,
      currency,
      plugins,
      bindings,
      discovery,
      translations,
      history,
      serverDown,
    } = this.props;



    if (serverDown) {
      return <div>Error: server is down.</div>;
    }

    if (!translations) {
      // We don't know the locale, so we use English as backup
      // return (<SystemSkeleton />);
      /** kware start edit */
      return <OrgLoader size="large" />;
      /** kware end edit */
    }

    const stripes = new Stripes({
      logger,
      store,
      epics,
      config,
      okapi,
      withOkapi: this.withOkapi,
      setToken: (val) => {
        store.dispatch(setOkapiToken(val));
      },
      setIsAuthenticated: (val) => {
        store.dispatch(setIsAuthenticated(val));
      },
      actionNames,
      locale,
      timezone,
      currency,
      metadata,
      icons,
      /* kware start editing --
       * Adding setTranslation func to  the stripes object, so we can update translation on the fly enytime
       */
      setTranslations: (newTrans) => {
        this.setLocaleTranslations(store, newTrans);
      },
      /** kware end edit section */
      setLocale: (localeValue) => {
        loadTranslations(store, localeValue, defaultTranslations);
      },
      setTimezone: (timezoneValue) => {
        store.dispatch(setTimezone(timezoneValue));
      },
      setCurrency: (currencyValue) => {
        store.dispatch(setCurrency(currencyValue));
      },
      updateUser: (userValue) => {
        store.dispatch(updateCurrentUser(userValue));
      },
      plugins: plugins || {},
      setSinglePlugin: (key, value) => {
        store.dispatch(setSinglePlugin(key, value));
      },
      bindings,
      setBindings: (val) => {
        store.dispatch(setBindings(val));
      },
      discovery,
      user: {
        user: currentUser,
        perms: currentPerms,
      },
      connect(X) {
        return X;
      },
    });

    return (
      <ErrorBoundary>
        <ConnectContext.Provider
          value={{ addReducer: this.addReducer, addEpic: this.addEpic, store }}
        >
          <ApolloProvider client={this.apolloClient}>
            <QueryClientProvider client={this.reactQueryClient}>
              <IntlProvider
                locale={locale}
                /* kware start editing --
                 * After switching locale from the top navbar while we are in the editing form, we will lose all the values entered
                 * in this form, and notice that the page is completely reloaded because the IntlProvider has a key = {locale}.
                 * In fact, all we really need when switching locale is to re-display the translations corresponding to the target locale,
                 * and since the IntlProvider always takes the message props from the translation global state, so we are always sure to change
                 * the translations when switching locale, so we don't need to re-load the page to show the locale translations. Download the entire page after switching locale.
                 * Note: This modification will have an effect in most applications, for example the appearance of application names in the top navbar
                 * and also in the settings.
                 */
                // key={locale}
                /* kware end editing */
                timeZone={timezone}
                currency={currency}
                messages={translations}
                textComponent={Fragment}
                onError={config?.suppressIntlErrors ? () => {} : undefined}
                onWarn={config?.suppressIntlWarnings ? () => {} : undefined}
                defaultRichTextElements={this.defaultRichTextElements}
              >
                <RootWithIntl
                  stripes={stripes}
                  token={token}
                  isAuthenticated={isAuthenticated}
                  disableAuth={disableAuth}
                  history={history}
                />
              </IntlProvider>
            </QueryClientProvider>
          </ApolloProvider>
        </ConnectContext.Provider>
      </ErrorBoundary>
    );
  }
}

Root.childContextTypes = {
  addReducer: PropTypes.func,
  addEpic: PropTypes.func,
};

Root.propTypes = {
  store: PropTypes.shape({
    subscribe: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    getState: PropTypes.func.isRequired,
    replaceReducer: PropTypes.func.isRequired,
  }),
  token: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  disableAuth: PropTypes.bool.isRequired,
  logger: PropTypes.object.isRequired,
  currentPerms: PropTypes.object,
  currentUser: PropTypes.object,
  epics: PropTypes.object,
  locale: PropTypes.string,
  defaultTranslations: PropTypes.object,
  timezone: PropTypes.string,
  currency: PropTypes.string,
  translations: PropTypes.object,
  modules: PropTypes.shape({
    app: PropTypes.arrayOf(PropTypes.object),
  }),
  plugins: PropTypes.object,
  bindings: PropTypes.object,
  config: PropTypes.object,
  okapi: PropTypes.shape({
    url: PropTypes.string,
    tenant: PropTypes.string,
    withoutOkapi: PropTypes.bool,
  }),
  actionNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  discovery: PropTypes.shape({
    modules: PropTypes.object,
    interfaces: PropTypes.object,
    isFinished: PropTypes.bool,
  }),
  history: PropTypes.shape({
    action: PropTypes.string.isRequired,
    length: PropTypes.number.isRequired,
    location: PropTypes.object.isRequired,
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
  }),
  okapiReady: PropTypes.bool,
  serverDown: PropTypes.bool,
};

Root.defaultProps = {
  history: createBrowserHistory(),
  // TODO: remove after locale is accessible from a global config / public url
  // locale: 'en-US',
  /** kware start edit */
  locale: 'ar',
  /** kware end edit */
  timezone: 'UTC',
  currency: 'USD',
  okapiReady: false,
  serverDown: false,
};

function mapStateToProps(state) {
  return {
    bindings: state.okapi.bindings,
    currency: state.okapi.currency,
    currentPerms: state.okapi.currentPerms,
    currentUser: state.okapi.currentUser,
    discovery: state.discovery,
    isAuthenticated: state.okapi.isAuthenticated,
    locale: state.okapi.locale,
    okapi: state.okapi,
    okapiReady: state.okapi.okapiReady,
    plugins: state.okapi.plugins,
    serverDown: state.okapi.serverDown,
    timezone: state.okapi.timezone,
    token: state.okapi.token,
    translations: state.okapi.translations,
  };
}

export default connect(mapStateToProps)(withModules(Root));
